// src/pages/Admin/VotePage/utils/voteAuthDebug.js

const safeJsonParse = (value) => {
  if (!value || typeof value !== "string") {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

export const decodeJwtPayload = (token) => {
  if (!token || typeof token !== "string") {
    return null;
  }

  try {
    const parts = token.split(".");

    if (parts.length < 2) {
      return null;
    }

    const base64Url = parts[1];

    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");

    const paddedBase64 = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");

    const decodedBinary = window.atob(paddedBase64);

    const decodedText = decodeURIComponent(
      decodedBinary
        .split("")
        .map(
          (character) =>
            `%${character.charCodeAt(0).toString(16).padStart(2, "0")}`,
        )
        .join(""),
    );

    return JSON.parse(decodedText);
  } catch (error) {
    console.error("[VOTE AUTH FRONT] Impossible de décoder le JWT :", error);

    return null;
  }
};

export const extractUserId = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value === "number" || typeof value === "string") {
    const directId = Number(value);

    return Number.isFinite(directId) ? directId : null;
  }

  const rawId =
    value.id ??
    value.userId ??
    value.user_id ??
    value.id_user ??
    value.sub ??
    value.data?.id ??
    value.data?.userId ??
    value.data?.user_id ??
    value.user?.id ??
    value.user?.userId ??
    value.user?.user_id ??
    null;

  const parsedId = Number(rawId);

  return Number.isFinite(parsedId) ? parsedId : null;
};

export const extractRoleId = (value) => {
  if (!value || typeof value !== "object") {
    return null;
  }

  const rawRoleId =
    value.role_id ??
    value.roleId ??
    value.id_role ??
    value.role?.id ??
    value.Role?.id ??
    value.data?.role_id ??
    value.data?.roleId ??
    value.user?.role_id ??
    value.user?.roleId ??
    value.user?.Role?.id ??
    null;

  if (rawRoleId === null || rawRoleId === undefined || rawRoleId === "") {
    return null;
  }

  const parsedRoleId = Number(rawRoleId);

  return Number.isFinite(parsedRoleId) ? parsedRoleId : null;
};

export const getStoredUserCandidates = () => {
  const storageKeys = [
    "user",
    "userInfo",
    "currentUser",
    "connectedUser",
    "authUser",
  ];

  return storageKeys.reduce((accumulator, key) => {
    const rawValue = localStorage.getItem(key);

    const parsedValue = safeJsonParse(rawValue);

    if (rawValue != null) {
      accumulator[key] = {
        rawValue,
        parsedValue,
        userId: extractUserId(parsedValue),
        roleId: extractRoleId(parsedValue),
      };
    }

    return accumulator;
  }, {});
};

export const getVoteAuthDebug = (contextValue = null) => {
  const token = localStorage.getItem("token");

  const jwtPayload = decodeJwtPayload(token);

  const contextUser =
    contextValue?.user ??
    contextValue?.userInfo ??
    contextValue?.currentUser ??
    contextValue?.connectedUser ??
    contextValue?.authUser ??
    null;

  const storedUsers = getStoredUserCandidates();

  const firstStoredUser =
    Object.values(storedUsers).find((item) => item?.parsedValue)?.parsedValue ??
    null;

  const contextUserId = extractUserId(contextUser);

  const contextRoleId = extractRoleId(contextUser);

  const tokenUserId = extractUserId(jwtPayload);

  const tokenRoleId = extractRoleId(jwtPayload);

  const storedUserId = extractUserId(firstStoredUser);

  const storedRoleId = extractRoleId(firstStoredUser);

  const resolvedUserId = contextUserId ?? tokenUserId ?? storedUserId ?? null;

  const resolvedRoleId = contextRoleId ?? tokenRoleId ?? storedRoleId ?? null;

  return {
    tokenPresent: Boolean(token),

    tokenPreview: token ? `${token.slice(0, 12)}...` : null,

    jwtPayload,

    contextValue,
    contextUser,

    storedUsers,

    contextUserId,
    contextRoleId,

    tokenUserId,
    tokenRoleId,

    storedUserId,
    storedRoleId,

    resolvedUserId,
    resolvedRoleId,

    isAdmin: Number(resolvedRoleId) === 1,
  };
};

export const logVoteAuthDebug = (label, contextValue = null) => {
  const debug = getVoteAuthDebug(contextValue);

  console.group(`[VOTE AUTH FRONT] ${label}`);

  console.log("Token présent :", debug.tokenPresent);

  console.log("Aperçu token :", debug.tokenPreview);

  console.log("Payload JWT décodé :", debug.jwtPayload);

  console.log("UserContext complet :", debug.contextValue);

  console.log("Utilisateur trouvé dans UserContext :", debug.contextUser);

  console.log("Utilisateurs trouvés dans localStorage :", debug.storedUsers);

  console.table({
    "ID depuis UserContext": debug.contextUserId,

    "Rôle depuis UserContext": debug.contextRoleId,

    "ID depuis JWT": debug.tokenUserId,

    "Rôle depuis JWT": debug.tokenRoleId,

    "ID depuis localStorage": debug.storedUserId,

    "Rôle depuis localStorage": debug.storedRoleId,

    "ID finalement retenu": debug.resolvedUserId,

    "Rôle finalement retenu": debug.resolvedRoleId,
  });

  if (debug.isAdmin) {
    console.log(
      "%cUtilisateur identifié comme administrateur sur le front.",
      "color: #16a34a; font-weight: bold;",
    );
  } else {
    console.warn(
      "Utilisateur non identifié comme administrateur sur le front.",
      {
        resolvedUserId: debug.resolvedUserId,
        resolvedRoleId: debug.resolvedRoleId,
      },
    );
  }

  console.groupEnd();

  return debug;
};
